import { Component, ErrorInfo, PropsWithChildren, ReactNode } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { captureException } from '@sentry/react';
import ErrorFallback from 'typescript/components/error_fallback';

interface ShopErrorBoundaryProps extends PropsWithChildren {
  appName: string;
  fallback?: ReactNode;
}

interface ShopErrorBoundaryState {
  hasError: boolean;
}

const DOM_MUTATION_METHOD_PATTERN = /\bremovechild\b/i;
const NATIVE_STACK_FRAME_PATTERN = /\bnative\b|<anonymous>/i;

interface ErrorDetails {
  name?: unknown;
  message?: unknown;
  stack?: unknown;
}

/**
 * Browser extensions and translators can remove a React-owned node before
 * React tries to remove it. React can retry these commit-time failures, so
 * they should not replace the entire app with the error fallback.
 */
export const isRecoverableDomMutationError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false;

  const { name, message, stack } = error as ErrorDetails;
  if (name !== 'NotFoundError') return false;

  const stackString = typeof stack === 'string' ? stack : '';
  const messageString = typeof message === 'string' ? message : '';
  const hasNativeMutationFrame = stackString
    .split('\n')
    .some(
      (line) =>
        DOM_MUTATION_METHOD_PATTERN.test(line) &&
        NATIVE_STACK_FRAME_PATTERN.test(line),
    );

  // Chromium includes the DOM method in the error message, even when the
  // native stack frame is omitted.
  return (
    hasNativeMutationFrame || DOM_MUTATION_METHOD_PATTERN.test(messageString)
  );
};

/**
 * Top-level error boundary for Shop React apps.
 * Catches render-time errors, reports non-recoverable errors to both Datadog
 * RUM (via addError, which feeds Error Tracking and supports monitors) and
 * Sentry, then renders a fallback UI so the page doesn't go blank. Known
 * external DOM mutation errors are retried without rendering the fallback.
 *
 * This must be a class component because React's error boundary API
 * (getDerivedStateFromError / componentDidCatch) has no hook equivalent.
 */
class ShopErrorBoundary extends Component<
  ShopErrorBoundaryProps,
  ShopErrorBoundaryState
> {
  state: ShopErrorBoundaryState = { hasError: false };

  // A single broken client (e.g. an extension mutating the DOM out from
  // under React) can throw the same error many times in one commit — most
  // visibly `removeChild` NotFoundErrors during subtree deletion. Report
  // each distinct error once per mount so one session can't flood the
  // error-tracking counts. Distinct errors are still all reported.
  reportedErrorKeys = new Set<string>();

  static getDerivedStateFromError(error: unknown): ShopErrorBoundaryState {
    return { hasError: !isRecoverableDomMutationError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isRecoverableDomMutationError(error)) {
      return;
    }

    const errorKey =
      error && typeof error === 'object'
        ? `${(error as Error).name}: ${(error as Error).message}`
        : String(error);
    if (this.reportedErrorKeys.has(errorKey)) {
      return;
    }
    this.reportedErrorKeys.add(errorKey);

    datadogRum.addError(error, {
      source: 'react-error-boundary',
      appName: this.props.appName,
      pageUrl: window.location.href,
      componentStack: errorInfo.componentStack,
    });

    captureException(error, {
      extra: {
        appName: this.props.appName,
        componentStack: errorInfo.componentStack,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <ErrorFallback />;
    }
    return this.props.children;
  }
}

export default ShopErrorBoundary;
